import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  ShieldCheck,
  Search,
  Printer,
} from 'lucide-react';
import { posApi, usersApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import ReportExportButton from '@/components/reports/ReportExportButton';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { format } from 'date-fns';
import clsx from 'clsx';

interface ManagerCashEntry {
  date: string;
  timestamp: string;
  type: string;
  category: 'IN' | 'OUT';
  reference: string;
  party: string;
  manager: string;
  manager_user: string;
  description: string;
  in: number;
  out: number;
  balance: number;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    return format(new Date(d), 'dd-MMM-yyyy');
  } catch {
    return d;
  }
};

const formatTime = (t?: string) => {
  if (!t) return '';
  try {
    return format(new Date(t), 'hh:mm a');
  } catch {
    return '';
  }
};

export default function ManagerCashReportSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedManager, setSelectedManager] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Fetch managers list
  const { data: managersRes } = useQuery({
    queryKey: ['company_managers'],
    queryFn: () => usersApi.getManagers(),
  });
  const managers = (managersRes?.data?.message as any[]) || [];

  // Fetch Manager Cash Custody Report
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['managerCashReport', from_date, to_date, selectedManager],
    queryFn: async () => {
      const res = await posApi.getManagerCashReport({
        from_date,
        to_date,
        manager: selectedManager || undefined,
      });
      return res.data.message;
    },
  });

  const allEntries: ManagerCashEntry[] = data?.entries || [];
  const opening = data?.opening_balance ?? 0;
  const totalIn = data?.total_in ?? 0;
  const totalOut = data?.total_out ?? 0;
  const closing = data?.closing_balance ?? 0;

  // Filter entries in UI by type / search term
  const filteredEntries = allEntries.filter((e) => {
    if (filterType === 'in' && e.category !== 'IN') return false;
    if (filterType === 'out' && e.category !== 'OUT') return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchDesc = (e.description || '').toLowerCase().includes(term);
      const matchParty = (e.party || '').toLowerCase().includes(term);
      const matchRef = (e.reference || '').toLowerCase().includes(term);
      const matchType = (e.type || '').toLowerCase().includes(term);
      const matchManager = (e.manager || '').toLowerCase().includes(term);
      if (!matchDesc && !matchParty && !matchRef && !matchType && !matchManager) return false;
    }
    return true;
  });

  const handleExport = () => {
    if (!data) return;
    const lines = [
      'Date,Time,Manager,Type,Category,Reference,Source/Destination,Description,Cash In,Cash Out,Running Balance',
      `,,,,Opening Balance,,,,,${data.opening_balance ?? 0}`,
      ...(filteredEntries || []).map((row) =>
        [
          formatDate(row.date),
          formatTime(row.timestamp),
          `"${(row.manager || '').replace(/"/g, '""')}"`,
          `"${(row.type || '').replace(/"/g, '""')}"`,
          row.category,
          row.reference || '',
          `"${(row.party || '').replace(/"/g, '""')}"`,
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.in || 0,
          row.out || 0,
          row.balance ?? 0,
        ].join(','),
      ),
      `,,,,Closing Balance,,,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `manager_cash_custody_${from_date}_${to_date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const getBadgeClass = (type: string, category: 'IN' | 'OUT') => {
    if (category === 'IN') {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (type.includes('Owner')) {
      return 'bg-purple-50 text-purple-700 border-purple-200';
    }
    if (type.includes('Bank')) {
      return 'bg-blue-50 text-blue-700 border-blue-200';
    }
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 space-y-4">
        <div className="flex flex-wrap gap-4 justify-between items-end">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="flex flex-wrap items-center gap-3">
            {/* Manager Selector */}
            <div className="min-w-[200px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Select Manager
              </label>
              <select
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                className="w-full text-sm border-gray-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Managers</option>
                {managers.map((m) => (
                  <option key={m.user} value={m.user}>
                    {m.full_name} ({m.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end gap-2 pt-5">
              <button
                type="button"
                onClick={() => void refetch()}
                className="px-3.5 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              >
                Refresh
              </button>
              <ReportExportButton onClick={handleExport} disabled={!data || isLoading} />
              <button
                type="button"
                onClick={handlePrint}
                className="px-3.5 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium flex items-center gap-1.5"
              >
                <Printer className="w-4 h-4" />
                <span>Print</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search & Category Filter Sub-bar */}
        <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-1.5 bg-gray-50 p-1 rounded-xl border border-gray-200 text-xs">
            <button
              type="button"
              onClick={() => setFilterType('all')}
              className={clsx(
                'px-3 py-1.5 rounded-lg font-medium transition-colors',
                filterType === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              All Transactions ({allEntries.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterType('in')}
              className={clsx(
                'px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1',
                filterType === 'in'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-emerald-700',
              )}
            >
              <ArrowDownLeft className="w-3.5 h-3.5" />
              Cash IN
            </button>
            <button
              type="button"
              onClick={() => setFilterType('out')}
              className={clsx(
                'px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1',
                filterType === 'out'
                  ? 'bg-amber-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-amber-700',
              )}
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
              Cash OUT
            </button>
          </div>

          <div className="relative min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search description, reference, cashier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs border-gray-200 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {t('reports.loadFailed', 'Failed to load report data')}
        </div>
      )}

      {/* KPI Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Opening */}
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between text-gray-500">
            <span className="text-xs font-semibold uppercase tracking-wider">Opening Float</span>
            <div className="p-2 bg-gray-100 rounded-lg text-gray-700">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xl font-bold text-gray-900 leading-tight">
              {formatMoney(opening)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Cash brought forward</p>
          </div>
        </div>

        {/* Total In */}
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between text-emerald-600">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Received (IN)</span>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <ArrowDownLeft className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xl font-bold text-emerald-700 leading-tight">
              +{formatMoney(totalIn)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Cashiers & float collected</p>
          </div>
        </div>

        {/* Total Out */}
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between text-amber-600">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Disbursed (OUT)</span>
            <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xl font-bold text-amber-700 leading-tight">
              -{formatMoney(totalOut)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Owner handovers, bank, expenses</p>
          </div>
        </div>

        {/* Current Custody Closing */}
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between text-blue-600">
            <span className="text-xs font-semibold uppercase tracking-wider">Current Cash in Hand</span>
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <p
              className={clsx(
                'text-xl font-bold leading-tight',
                closing >= 0 ? 'text-gray-900' : 'text-red-600',
              )}
            >
              {formatMoney(closing)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {selectedManager ? 'Holding by selected manager' : 'All managers active custody'}
            </p>
          </div>
        </div>
      </div>

      {/* Transactions Ledger Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-gray-50/70 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
              Cash Transactions & Custody Ledger
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-semibold">
              {filteredEntries.length} Records
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {selectedManager ? `Showing: ${data?.manager}` : 'Consolidated view of all managers'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 text-gray-600 font-semibold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="px-4 py-3 text-left">Date & Time</th>
                <th className="px-4 py-3 text-left">Manager</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Source / Destination</th>
                <th className="px-4 py-3 text-left">Reference / Notes</th>
                <th className="px-4 py-3 text-right">Cash IN (+)</th>
                <th className="px-4 py-3 text-right">Cash OUT (-)</th>
                <th className="px-4 py-3 text-right">Running Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {/* Opening Balance Row */}
              <tr className="bg-gray-50/60 font-medium text-gray-500">
                <td className="px-4 py-2.5" colSpan={5}>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-gray-400" />
                    <span>Opening Balance (Prior to {formatDate(from_date)})</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">—</td>
                <td className="px-4 py-2.5 text-right">—</td>
                <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                  {formatMoney(opening)}
                </td>
              </tr>

              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    Loading manager cash ledger...
                  </td>
                </tr>
              ) : filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    No cash transactions found for the selected period & filters.
                  </td>
                </tr>
              ) : (
                filteredEntries.map((row, idx) => (
                  <tr key={`${row.reference}-${idx}`} className="hover:bg-gray-50/70 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{formatDate(row.date)}</div>
                      <div className="text-[11px] text-gray-400">{formatTime(row.timestamp)}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                      {row.manager}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1',
                          getBadgeClass(row.type, row.category),
                        )}
                      >
                        {row.category === 'IN' ? (
                          <ArrowDownLeft className="w-3 h-3" />
                        ) : (
                          <ArrowUpRight className="w-3 h-3" />
                        )}
                        <span>{row.type}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate" title={row.party}>
                      {row.party}
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="text-gray-900 truncate font-medium" title={row.description}>
                        {row.description}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate" title={row.reference}>
                        Ref: {row.reference}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                      {row.in > 0 ? `+${formatMoney(row.in)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-amber-700">
                      {row.out > 0 ? `-${formatMoney(row.out)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">
                      {formatMoney(row.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {/* Table Footer with Final Balances */}
            <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-bold text-gray-900 text-xs">
              <tr>
                <td colSpan={5} className="px-4 py-3 text-left">
                  Period Totals & Closing Balance
                </td>
                <td className="px-4 py-3 text-right text-emerald-700">
                  +{formatMoney(totalIn)}
                </td>
                <td className="px-4 py-3 text-right text-amber-700">
                  -{formatMoney(totalOut)}
                </td>
                <td className="px-4 py-3 text-right text-base text-gray-900">
                  {formatMoney(closing)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
